import * as React from 'react';
import {Suspense, useDeferredValue, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {ArrowLeft} from 'lucide-react';
import EngagementBuilder from '../../engagement/EngagementBuilder';
import {inputClass, ghostButton} from './lib';
import {NewCustomerForm} from './OpsCustomersPage';
import {CustomerResults} from './OpsReservationNewPage';

type Customer = { id: string; name: string; email?: string | null; phone?: string | null };

// Ops host for the engagement builder: staff pick (or create) the customer first,
// then build the engagement on their behalf.
const OpsEngagementNewPage: React.FC = () => {
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const [showNew, setShowNew] = useState(false);

    const customerStep = () => (
        <>
            <h2 className="text-2xl font-display font-bold mb-1">Who is it for?</h2>
            <p className="text-gray-400 text-sm mb-4">The engagement is filed under this customer and shows up in their portal if they've signed in before.</p>
            {customer ? (
                <div className="flex items-center gap-3 p-3 border border-lumina-accent bg-lumina-accent/5">
                    <span className="text-white font-semibold">{customer.name}</span>
                    {customer.email && <span className="text-gray-500 text-sm">{customer.email}</span>}
                    <button type="button" onClick={() => setCustomer(null)} className="ml-auto text-xs text-gray-400 hover:text-white">change</button>
                </div>
            ) : (
                <div className="space-y-3">
                    <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers…" className={inputClass}/>
                    <Suspense fallback={<p className="text-gray-500 text-xs">Searching…</p>}>
                        <CustomerResults search={deferredSearch} onPick={setCustomer}/>
                    </Suspense>
                    {showNew ? (
                        <NewCustomerForm onCreated={(c) => { setCustomer(c); setShowNew(false); }}/>
                    ) : (
                        <button type="button" onClick={() => setShowNew(true)} className={ghostButton}>New customer</button>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div>
            <Link to="/ops/engagements" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-white mb-4">
                <ArrowLeft className="w-3 h-3"/> Engagements
            </Link>
            <h1 className="text-3xl font-display font-bold mb-6">New engagement</h1>
            <EngagementBuilder
                host="ops"
                customerId={customer?.id}
                renderCustomerStep={customerStep}
                onSubmitted={() => navigate('/ops/engagements')}
            />
        </div>
    );
};

export default OpsEngagementNewPage;
